<template>
  <!-- ----------------- -->
  <!-- TOP ROW (HEADER)  -->
  <!-- ----------------- -->
  <div>
    <div class="mt-3">
      <div class="bb-sheet-header-row">
        <div class="bb-sheet-search-slot">
          <div class="vgt-wrap black-rhino bb-sheet-search-wrap">
            <div class="vgt-global-search vgt-clearfix bb-sheet-search-shell">
              <div class="vgt-global-search__input vgt-pull-left">
                <form
                  @submit.prevent
                  role="search"
                >
                  <label for="bb-sheet-search-input">
                    <span
                      aria-hidden="true"
                      class="input__icon"
                    >
                      <div class="magnifying-glass"></div>
                    </span>
                    <span class="sr-only">Search</span>
                  </label>
                  <input
                    id="bb-sheet-search-input"
                    v-model="searchQuery"
                    type="text"
                    class="vgt-input vgt-pull-left bb-sheet-search-input"
                    placeholder="Search..."
                  />
                </form>
              </div>
              <div class="vgt-global-search__actions vgt-pull-right"></div>
            </div>
          </div>
        </div>
        <p class="bb-sheet-title mb-0">{{ title }}</p>
      </div>
    </div>
    <!-- ------------------------- -->
    <!-- OVERLAY: item side panel -->
    <!-- ------------------------- -->
    <ItemSidePanel
      v-if="isSidePanelOpen"
      :item="character_item"
      :item_type="item_type"
      :character="character"
      @add-character-item="$emit('add-character-item', $event)"
      @update-character-item="$emit('update-character-item', $event)"
      @delete-character-item="$emit('delete-character-item', $event)"
      @close-item-panel="handleItemPanelClose"
      @confirm-item-ok="handleConfirmItemOk"
    />
    <!-- ------------------------ -->
    <!-- NOTIFICATIONS            -->
    <!-- ------------------------ -->
    <notifications
      width="500"
      group="bb-confirm"
      :speed="100"
      :duration="1000"
      position="top right"
      classes="vue-notification"
    />
    <!-- ------------------ -->
    <!--     item table     -->
    <!-- ------------------ -->
    <div v-if="items && character && headingsReady">
      <TableItems
        ref="TableItems"
        :headings="headings"
        :item_type="item_type"
        :items="items"
        :character="character"
        :search-query="searchQuery"
        @refresh-vertical-scroll-state="$emit('refresh-vertical-scroll-state')"
        @update-item-qty="$emit('update-character-item', $event)"
        @show-character-item="handleShowItemPanel"
        @delete-character-item="$emit('delete-character-item', $event)"
      />
    </div>
  </div>
  <!-- <div v-if="!loadReady" class="bb-loader"></div> -->
  <!-- </div> -->
</template>

<script>
//========================================================
// scripts
//---------
import { inject } from 'vue'
import { mapGetters } from 'vuex'

import applyWeaponUsability from '../helpers/applyWeaponUsabilty'
import {
  getEffectiveItemType,
  getItemPropertySource,
  getNormalisedEquipmentType,
} from '../helpers/getEffectiveItemType'
import getWeaponDamageDice from '../helpers/getWeaponDamageDice'
import getItemSummaryCols from '../helpers/getItemSummaryCols'
import getItemDetailCols from '../helpers/getItemDetailCols'
import hasTempId from '../helpers/hasTempId'
import isEmpty from '../helpers/isEmpty'
import showNotification from '../helpers/showNotification'

import ItemSidePanel from '../components/ItemSidePanel.vue'
import TableItems from './TableItems.vue'

export default {
  name: 'CharacterSheetItems',
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // components
  //------------
  components: {
    TableItems,
    ItemSidePanel,
  },
  //========================================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
    item_type: {
      type: String,
      required: true,
    },
    errorFields: {
      type: Array,
      required: true,
    },
    toggleDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    active_tab: {
      type: String,
      required: false,
      default: '',
    },
    title: {
      type: String,
      required: true,
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      endpoint: '',
      local_slug: '',

      deleteInd: false,
      loadReady: false,
      initialLoad: true,
      isSidePanelOpen: false,
      headingsReady: false,
      searchQuery: '',

      character_item: null,

      items: [],
      errors: [],
      //
      name: 'ItemList',
      headings: [],
      //
      headings_common: [
        // -------------------------------------------------------------------
        // basic details
        {
          id: 0,
          label: '#',
          field: 'rowNumber',
          filterable: false,
          sortable: true,
          tdClass: 'text-center',
          width: '4em',
        },
        {
          id: 5,
          label: 'Item',
          field: 'name',
          width: '16em',
          filterable: false,
          sortable: true,
          formatFn: (value, row) => {
            return row.display_name || row.name
          },
        },
        {
          id: 6,
          label: 'Image',
          field: 'image',
          width: '6em',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 6.5,
          label: 'Base Item',
          field: 'base_item',
          width: '12em',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: true,
        },
        {
          id: 7,
          label: 'Type',
          field: 'type',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: true,
        },
        {
          id: 8,
          label: 'Equip Type',
          field: 'equipment_type',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: true,
        },
        {
          id: 10,
          label: 'Short Desc',
          field: 'short_description',
          // tdClass: 'text-nowrap',
          filterable: false,
          sortable: false,
          hidden: false,
          width: '16em',
        },
        {
          id: 101,
          label: 'Healing Type',
          field: 'healing_type',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          hidden: true,
          width: '7em',
        },
        {
          id: 102,
          label: 'Recovers',
          field: 'recovers',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          hidden: false,
          width: '7em',
        },
        {
          id: 104,
          label: 'Poison Type',
          field: 'poison_type',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          hidden: false,
          width: '7em',
        },
        {
          id: 105,
          label: 'Potency',
          field: 'pot',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          hidden: false,
          width: '6em',
        },
        {
          id: 106,
          label: 'Effect',
          field: 'effect',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          hidden: false,
          width: '12em',
        },
        {
          id: 103,
          label: 'Delay',
          field: 'delay',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          hidden: false,
          width: '6em',
        },
        {
          id: 107,
          label: 'Duration',
          field: 'duration',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          hidden: false,
          width: '7em',
        },
        {
          id: 108,
          label: 'Resist',
          field: 'resist',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          hidden: false,
          width: '10em',
        },
        // -------------------------------------------------------------------
        // flags
        {
          id: 15,
          label: '',
          field: 'fill0',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 35,
          label: 'Located',
          field: 'located_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 35,
          label: 'Held',
          field: 'held_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 100,
          label: 'Usable',
          field: 'usable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
        },
        {
          id: 20,
          label: 'Stored',
          field: 'stored_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 25,
          label: 'Worn',
          field: 'worn_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 27,
          label: 'Worn', // deliberately worn label to hide implementation detail of equipped vs worn in character item
          field: 'equipped_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 30,
          label: 'Attached',
          field: 'attached_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 35,
          label: 'Magic',
          field: 'magic_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        // -------------------------------------------------------------------
        // tally details
        {
          id: 40,
          label: '',
          field: 'fill1',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 45,
          label: 'Tally Qty',
          field: 'tally_qty',
          // type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          width: '5em',
        },
        {
          id: 50,
          label: 'Tally Adjust',
          field: 'adjust_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '5em',
        },
        {
          id: 55,
          label: 'Total Enc',
          field: 'tot_enc',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
          formatFn: (value) => {
            const numericValue = Number(value)
            return Number.isFinite(numericValue) ? numericValue.toFixed(2) : ''
          },
          width: '6em',
        },
        {
          id: 56,
          label: 'Value',
          field: 'value',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          width: '7em',
          hidden: true,
        },
        {
          id: 57,
          label: 'Unit Name',
          field: 'unit_name',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '7em',
          formatFn: (value, row) => {
            return row.countable ? value || '' : ''
          },
        },
        {
          id: 59,
          label: 'Max Units',
          field: 'max_unit_count',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
          formatFn: (value, row) => {
            return row.countable ? (value ?? '') : ''
          },
        },
        {
          id: 61,
          label: 'Cur Units',
          field: 'cur_unit_count',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
          formatFn: (value, row) => {
            return row.countable ? (value ?? '') : ''
          },
        },
        {
          id: 65,
          label: 'Campaign Ref',
          field: 'campaign_ref',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          formatFn: (value, row) => {
            return row.campaign_ref > 0 ? row.campaign_ref : ''
          },
          width: '6em',
        },
        // -------------------------------------------------------------------
        // container details
        {
          id: 70,
          label: '',
          field: 'fill2',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 72,
          label: 'Attached Items',
          field: 'attachment_count',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
          formatFn: (value, row) => {
            if (!row.attach_point) return ''
            return value ?? ''
          },
        },
        {
          id: 73,
          label: 'Attached',
          field: 'attachment_items_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 75,
          label: 'Contents',
          field: 'contents_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 85,
          label: 'Container Size',
          field: 'container_size_short',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 80,
          label: 'Enc Capacity',
          field: 'container_capacity',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
          formatFn: this.global.oneDecimal,
        },
        {
          id: 90,
          label: 'Direct Items',
          field: 'contents_count_direct',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
          formatFn: (value, row) => {
            // hide for non-storage items
            if (!row.storage) return ''
            return value
          },
        },
        {
          id: 90,
          label: 'Contents Count',
          field: 'contents_count_total',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
          formatFn: (value, row) => {
            // hide for non-storage items
            if (!row.storage) return ''
            return value
          },
        },
        {
          id: 95,
          label: 'Contents Enc',
          field: 'contents_enc',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
          formatFn: (value, row) => {
            // hide for non-storage items, otherwise use the ENC formatter
            if (!row.storage) return ''
            if (this.global && typeof this.global.twoDecimal === 'function') {
              return this.global.twoDecimal(value)
            }
            const num = Number(value)
            return Number.isNaN(num) ? '' : num.toFixed(2)
          },
        },
        // -------------------------------------------------------------------
        // armour/weapon details
        {
          id: 96,
          label: '',
          field: 'fill3',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 97,
          label: 'Armour Locations',
          field: 'coverage',
          tdClass: 'text-left',
          filterable: false,
          sortable: false,
          hidden: false,
          width: '18em',
        },
        {
          id: 100,
          label: 'Damage',
          field: 'damage_dice',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
          width: '14em',
        },
        {
          id: 98,
          label: 'AP',
          field: 'ap',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
        },
        {
          id: 99,
          label: 'HP',
          field: 'hp',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
        },
        {
          id: 99,
          label: 'Dex Pen %',
          field: 'dex_pen',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
        },
        {
          id: 100,
          label: 'Layer',
          field: 'layer',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
        },
        {
          id: 100,
          label: 'Damage',
          field: 'damage_dice',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
        },
        {
          id: 20,
          label: 'Impale',
          field: 'impale_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 25,
          label: 'Set Charge',
          field: 'set_charge_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 100,
          label: 'Load',
          field: 'load',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
          formatFn: (value, row) => {
            if (!row.load) return ''
            return value + ' CA'
          },
        },
        {
          id: 100,
          label: 'Range',
          field: 'range',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
          formatFn: (value, row) => {
            if (!row.load) return ''
            return value + 'm'
          },
        },
        // -------------------------------------------------------------------
        //  artefact details
        {
          id: 100,
          label: 'Power Storage',
          field: 'pwr_storage',
          tdClass: 'text-center',
          width: '6em',
          filterable: true,
          sortable: true,
          hidden: false,
          formatFn: (value) => {
            const numericValue = Number(value)
            return Number.isFinite(numericValue) && numericValue > 0
              ? numericValue
              : ''
          },
        },
        {
          id: 100,
          label: 'Rune',
          field: 'rune_name',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 100,
          label: 'Type',
          field: 'artefact_type',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 100,
          label: 'Material',
          field: 'material',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 100,
          label: 'Quality',
          field: 'quality',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 100,
          label: 'Type',
          field: 'artefact_type',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 100,
          label: 'Age',
          field: 'age',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 100,
          label: 'Race',
          field: 'race',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
          // display race name instead of numeric id
          formatFn: (value, row) => {
            if (row?.race_name) {
              return row.race_name
            }

            const getRaceById = this.$store.getters['races/getRaceById']
            const race =
              typeof getRaceById === 'function' ? getRaceById(value) : null
            // const races = this.$store.getters['races/getRaces'] || []
            // const race = races.find((r) => r.id === value)
            return race ? race.name : 'Unknown'
          },
        },
        // -------------------------------------------------------------------
        //  item images (details)
        {
          id: 103,
          label: '',
          field: 'fill4',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 112,
          label: 'Held Flag',
          field: 'held_yes',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
          formatFn: (_value, row) => {
            return row?.held ? 'Yes' : ''
          },
        },
        {
          id: 135,
          label: 'Starter',
          field: 'starter_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 145,
          label: 'Tallyable',
          field: 'tallyable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 160,
          label: 'Countable',
          field: 'countable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 155,
          label: 'Wearable',
          field: 'wearable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 165,
          label: 'Storeable',
          field: 'storeable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 175,
          label: 'Storage',
          field: 'storage_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 195,
          label: 'Attach Point',
          field: 'attach_point_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 185,
          label: 'Attachable',
          field: 'attachable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        // --------------------------------------------------------------
        // misc field (hidden)
        {
          id: 105,
          label: 'Id',
          field: 'id',
          type: 'number',
          tdClass: 'text-nowrap',
          hidden: true,
        },
        {
          id: 110,
          label: 'Static Id',
          field: 'static_id',
          type: 'number',
          tdClass: 'text-nowrap',
          hidden: true,
        },
        {
          id: 101,
          label: 'Unusable Reasons',
          field: 'unusable_reasons',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        // --------------------------------------------------------------
        // item flags (hidden)
        {
          id: 112,
          label: 'Held Flag',
          field: 'held',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
          hidden: false,
        },
        {
          id: 115,
          label: 'Worn Flag',
          field: 'worn',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 117,
          label: 'Equipped Flag',
          field: 'equipped',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 120,
          label: 'Attached Flag',
          field: 'attached',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 125,
          label: 'Stored Flag',
          field: 'stored',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 140,
          label: 'Starter Flag',
          field: 'starter',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 130,
          label: 'Magic Flag',
          field: 'magic',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 150,
          label: 'Tallyable Flag',
          field: 'tallyable',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 170,
          label: 'Storeable Flag',
          field: 'storeable',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 160,
          label: 'Wearable Flag',
          field: 'wearable',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 190,
          label: 'Attachable Flag',
          field: 'attachable',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 200,
          label: 'Attach Point Flag',
          field: 'attach_point',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
        },
        {
          id: 180,
          label: 'Storage Flag',
          field: 'storage',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 210,
          label: 'Broken Flag',
          field: 'broken',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 205,
          label: 'No Location Flag',
          field: 'no_location',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 100,
          label: 'Impale Flag',
          field: 'impale',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 100,
          label: 'Usable Status',
          field: 'usable_status',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
        },
      ],
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    toggleDetails(newVal) {
      // toggleDetails() {
      this.toggleDetailsHandler(newVal)
    },
    active_tab() {
      //------------------------------------------
      // show details or summary cols where table
      //------------------------------------------
      this.toggleDetailsHandler(this.toggleDetails)
    },
    //---------------------------------------------------------------------
    // watch character so adding/removing items updates table immediately
    //---------------------------------------------------------------------
    character: {
      handler(newChar) {
        console.log('=====================================')
        console.log('WATCH CHARACTER ITEMS....', newChar.items)
        if (!newChar) return
        // reload filtered/sorted items for the new/updated character
        this.loadCharacterItemsByType(newChar)
      },
      deep: true,
      immediate: true,
    },
  },
  //========================================================
  // computed
  //----------
  computed: {
    //--------
    // mapped
    //--------
    ...mapGetters('characters', {
      getStaticItemById: 'getStaticItemById',
      getCurrencies: 'getCurrencies',
    }),
    api_request() {
      return window.bbAPIRequest(this.endpoint, this.local_slug, this.deleteInd)
    },
    isInvertedSidePanelOpen() {
      return !this.isSidePanelOpen
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    buildPendingItemDisplaySignature(item) {
      if (!item || typeof item !== 'object') {
        return ''
      }

      return JSON.stringify({
        item: item.item ?? null,
        type: item.type ?? null,
        artefact_type: item.artefact_type ?? null,
        name: item.name ?? '',
        display_name: item.display_name ?? '',
        tally_qty: item.tally_qty ?? null,
        cur_unit_count: item.cur_unit_count ?? null,
        value_lower: item.value_lower ?? null,
        value_upper: item.value_upper ?? null,
        currency: item.currency ?? null,
        player_notes: item.player_notes ?? '',
        container: item.container ?? null,
        worn_location: item.worn_location ?? null,
        attach_location: item.attach_location ?? null,
        held: item.held ?? false,
        worn: item.worn ?? false,
        equipped: item.equipped ?? false,
      })
    },
    collapseTempPersistedDisplayDuplicates(items) {
      if (!Array.isArray(items) || items.length < 2) {
        return items
      }

      const persistedBySignature = new Map()

      items.forEach((item) => {
        const itemId = item?.id ?? null
        if (itemId == null || hasTempId(itemId)) {
          return
        }

        const signature = this.buildPendingItemDisplaySignature(item)
        if (!signature || persistedBySignature.has(signature)) {
          return
        }

        persistedBySignature.set(signature, item)
      })

      if (persistedBySignature.size === 0) {
        return items
      }

      return items.filter((item) => {
        const itemId = item?.id ?? null
        if (itemId == null || !hasTempId(itemId)) {
          return true
        }

        const signature = this.buildPendingItemDisplaySignature(item)
        if (!signature) {
          return true
        }

        return !persistedBySignature.has(signature)
      })
    },
    formatValueNumber(value) {
      const numericValue = Number(value)

      if (!Number.isFinite(numericValue)) {
        return ''
      }

      return Number.isInteger(numericValue)
        ? `${numericValue}`
        : numericValue.toFixed(2).replace(/\.?0+$/, '')
    },
    getCurrencySuffix(rawCurrency) {
      if (
        rawCurrency === null ||
        rawCurrency === undefined ||
        rawCurrency === ''
      ) {
        return ''
      }

      if (typeof rawCurrency === 'object') {
        return (
          rawCurrency.short_name || rawCurrency.name || rawCurrency.id || ''
        )
      }

      const currencies = Array.isArray(this.getCurrencies)
        ? this.getCurrencies
        : []
      const matchedCurrency = currencies.find(
        (currency) =>
          currency &&
          (currency.id === rawCurrency ||
            currency.short_name === rawCurrency ||
            currency.name === rawCurrency),
      )

      return matchedCurrency?.short_name || matchedCurrency?.name || rawCurrency
    },
    buildItemValueDisplay(item) {
      const lowerNumericValue = Number(item?.value_lower)
      const upperNumericValue = Number(item?.value_upper)

      const hasLowerRaw =
        item?.value_lower !== null &&
        item?.value_lower !== undefined &&
        item?.value_lower !== '' &&
        Number.isFinite(lowerNumericValue)
      const hasUpperRaw =
        item?.value_upper !== null &&
        item?.value_upper !== undefined &&
        item?.value_upper !== '' &&
        Number.isFinite(upperNumericValue)

      const hasLower = hasLowerRaw && lowerNumericValue !== 0
      const hasUpper = hasUpperRaw && upperNumericValue !== 0

      if (!hasLowerRaw && !hasUpperRaw) {
        return ''
      }

      if (!hasLower && !hasUpper) {
        return ''
      }

      const lowerValue = hasLowerRaw
        ? this.formatValueNumber(item.value_lower)
        : ''
      const upperValue = hasUpper
        ? this.formatValueNumber(item.value_upper)
        : ''

      if (!lowerValue && !upperValue) {
        return ''
      }

      const currencySuffix = this.getCurrencySuffix(item?.currency)
      const valueDisplay = upperValue
        ? `${lowerValue}-${upperValue}${currencySuffix}`
        : `${lowerValue}${currencySuffix}`

      return item?.estimated_value ? `${valueDisplay} (E)` : valueDisplay
    },
    buildArtefactBaseItemDisplay(item) {
      const rawBaseItem = item?.artefact_base_item

      if (
        rawBaseItem === null ||
        rawBaseItem === undefined ||
        rawBaseItem === ''
      ) {
        return ''
      }

      if (typeof rawBaseItem === 'object') {
        return (
          rawBaseItem.name ||
          rawBaseItem.display_name ||
          rawBaseItem.label ||
          ''
        )
      }

      const staticBaseItem = this.getStaticItemById(rawBaseItem)
      return staticBaseItem?.name || ''
    },
    isWeaponOrArmour(item) {
      const itemType = item?.effective_type ?? item?.type
      return (
        item &&
        (itemType === 'Melee Weapon' ||
          itemType === 'Ranged Weapon' ||
          itemType === 'Armour')
      )
    },
    //========================================================
    // load character items by type
    //-------------------------------
    loadCharacterItemsByType(character) {
      console.log('-------------------------------')
      console.log('IN loadCharacterItemsByType item_type = ', this.item_type)

      // IMPORTANT: do not mutate character.items here, or we create
      // a reactive feedback loop. Work on shallow copies instead.

      const sourceItems =
        this.item_type === 'All'
          ? character.items
          : character.items.filter((i) => {
              const rowMatchesArtefactSubtype =
                i?.type === 'Artefact' && i?.artefact_type === this.item_type

              if (!i?.item) {
                return i?.type === this.item_type || rowMatchesArtefactSubtype
              }

              const static_item = this.getStaticItemById(i.item)
              if (!static_item) {
                return i?.type === this.item_type || rowMatchesArtefactSubtype
              }

              const effectiveType = getEffectiveItemType(
                static_item,
                this.getStaticItemById,
              )

              return (
                static_item.type === this.item_type ||
                effectiveType === this.item_type ||
                rowMatchesArtefactSubtype ||
                static_item.artefact_type === this.item_type
              )
            })

      const displayItems =
        this.collapseTempPersistedDisplayDuplicates(sourceItems)

      const strCur =
        typeof character.str_cur === 'number' ? character.str_cur : null
      const dexCur =
        typeof character.dex_cur === 'number' ? character.dex_cur : null

      const sortedItems = displayItems
        .map((i) => {
          const item = { ...i }
          // console.log('--------------------------------------------------------------')
          // console.log('LOADING CHARACTER ITEM = ', item.name, ' item = ', item)

          // treat equipped as worn for display purposes
          if (item.equipped !== undefined && item.equipped) {
            item.worn = item.equipped
          }

          if (item.item) {
            const static_item = this.getStaticItemById(item.item)
            // console.log('FOUND STATIC ITEM = ', static_item)

            if (static_item) {
              const effectiveType = getEffectiveItemType(
                static_item,
                this.getStaticItemById,
              )
              const propertySourceItem = getItemPropertySource(
                static_item,
                this.getStaticItemById,
              )

              // basic static fields
              if (item.enc === null || item.enc === undefined) {
                item.enc = static_item.enc
              }
              item.currency_cost = static_item.currency_cost
              item.static_id = static_item.id
              item.type = static_item.type
              item.actual_type = static_item.type
              item.effective_type = effectiveType || static_item.type
              item.artefact_base_item =
                item.artefact_base_item ??
                static_item.artefact_base_item ??
                null
              item.equipment_type = getNormalisedEquipmentType(
                static_item.equipment_type,
              )
              item.short_description =
                typeof item.short_description === 'string' &&
                item.short_description.trim() !== ''
                  ? item.short_description
                  : (static_item.short_description ?? '')
              item.description = static_item.description
              item.healing_type =
                propertySourceItem?.healing_type ?? item.healing_type
              item.recovers = propertySourceItem?.recovers ?? item.recovers
              item.delay = propertySourceItem?.delay ?? item.delay
              item.poison_type =
                propertySourceItem?.poison_type ?? item.poison_type
              item.pot = propertySourceItem?.pot ?? item.pot
              item.effect = propertySourceItem?.effect ?? item.effect
              item.duration = propertySourceItem?.duration ?? item.duration
              item.resist = propertySourceItem?.resist ?? item.resist
              item.campaign_ref = static_item.campaign_ref ?? 0
              item.campaign =
                item.campaign ??
                static_item.campaign ??
                Number(item.campaign_ref ?? static_item.campaign_ref ?? 0) > 0
              item.tallyable = item.tallyable ?? static_item.tallyable ?? false
              item.countable = item.countable ?? static_item.countable ?? false
              item.item_traits = Array.isArray(item.item_traits)
                ? item.item_traits
                : (static_item.item_traits ?? [])

              if (item.countable) {
                const staticUnitName =
                  propertySourceItem?.unit_name ?? static_item.unit_name ?? null
                item.unit_name = item.unit_name ?? staticUnitName ?? ''

                const staticMaxUnitCount =
                  propertySourceItem?.max_unit_count ??
                  static_item.max_unit_count ??
                  0
                const rawTallyQty = Number(item.tally_qty ?? 0)
                const hasUnitName =
                  typeof item.unit_name === 'string'
                    ? item.unit_name.trim() !== ''
                    : !!item.unit_name
                item.max_unit_count =
                  item.max_unit_count ?? staticMaxUnitCount ?? 0
                item.cur_unit_count =
                  !Number.isNaN(rawTallyQty) && rawTallyQty <= 0 && hasUnitName
                    ? 0
                    : (item.cur_unit_count ?? item.max_unit_count ?? 0)
              }

              // item image (use static item image when character-item
              // image is not already set, so ItemCard previews or
              // in-flight edits on the character item take precedence)

              if (!item.image && static_item.image) {
                item.image = static_item.image
              }

              // melee / ranged / armour properties
              const itemIsArtefactWeapon =
                static_item.type === 'Artefact' &&
                (item.effective_type === 'Melee Weapon' ||
                  item.effective_type === 'Ranged Weapon')
              const weaponPropertySource =
                itemIsArtefactWeapon && Array.isArray(static_item.skills)
                  ? static_item
                  : propertySourceItem

              item.damage_dice =
                item.effective_type === 'Melee Weapon' ||
                item.effective_type === 'Ranged Weapon'
                  ? getWeaponDamageDice(
                      weaponPropertySource ?? item,
                      this.character?.skills,
                    )
                  : (propertySourceItem?.damage_dice ?? item.damage_dice)

              //----------------------------------------------
              // AP/HP handling differs for armour vs weapons
              //----------------------------------------------
              if (
                item.effective_type === 'Armour' &&
                propertySourceItem?.armour &&
                propertySourceItem.armour.armour_material
              ) {
                // ARMOUR: AP and HP are per-character, init from static item
                // but thereafter only changed at character-item level

                const matAp =
                  propertySourceItem.armour.armour_material.ap ??
                  propertySourceItem.ap ??
                  0
                const matHp =
                  propertySourceItem.armour.armour_material.hp ??
                  propertySourceItem.hp ??
                  0

                if (item.ap === null || item.ap === undefined) {
                  item.ap = matAp
                }
                if (item.hp === null || item.hp === undefined) {
                  item.hp = matHp
                }
              } else if (
                item.effective_type === 'Melee Weapon' ||
                item.effective_type === 'Ranged Weapon'
              ) {
                // WEAPONS: preserve any per-character AP/HP already stored
                // on the CharacterItem. Only seed from the static item when
                // the character-item value is still missing.

                const weaponAp = propertySourceItem?.ap ?? 0
                const weaponHp = propertySourceItem?.hp ?? 0

                if (item.ap === null || item.ap === undefined) {
                  item.ap = weaponAp
                }

                // only seed HP from static when not yet set on the
                // character-item so later per-character changes stick.
                if (item.hp === null || item.hp === undefined) {
                  item.hp = weaponHp
                }
              }

              // determine broken status weapons/armour HP
              if (this.isWeaponOrArmour(item)) {
                if (item.hp !== null && item.hp === 0) {
                  item.broken = true
                } else {
                  item.broken = false
                }
              } else {
                item.broken = false
              }

              // weapon-only props
              if (
                item.effective_type === 'Melee Weapon' ||
                item.effective_type === 'Ranged Weapon'
              ) {
                item.impale = propertySourceItem?.impale ?? item.impale
                item.set_charge =
                  propertySourceItem?.set_charge ?? item.set_charge
                item.load = propertySourceItem?.load ?? item.load
                item.range = propertySourceItem?.range ?? item.range

                // determine weapon usability/requirements
                applyWeaponUsability(
                  item,
                  weaponPropertySource,
                  character,
                  strCur,
                  dexCur,
                  item.effective_type,
                )

                // artefact props (static item values were committed or local if pending)
              }

              if (static_item.type === 'Artefact') {
                //------------------------------------------------------
                // Artefact props
                // - Use static item values to seed empty character-item
                //   fields on first load.
                // - Once a field has a non-empty value on the
                //   character-item (e.g. edited via ItemCard), keep that
                //   value so that in-flight edits are reflected in the
                //   table immediately, even before saves/refreshes.
                //------------------------------------------------------

                // artefact type (string)
                if (isEmpty(item.artefact_type)) {
                  item.artefact_type =
                    static_item.artefact_type ?? item.artefact_type
                }

                // material / quality / age (lookup strings)
                if (isEmpty(item.material)) {
                  item.material = static_item.material ?? item.material
                }
                if (isEmpty(item.quality)) {
                  item.quality = static_item.quality ?? item.quality
                }
                if (isEmpty(item.age)) {
                  item.age = static_item.age ?? item.age
                }

                if ((item.pwr_storage ?? 0) <= 0) {
                  item.pwr_storage = static_item.pwr_storage ?? item.pwr_storage
                }

                // normalise race to an id (or null) without assuming
                // either static_item.race or item.race are objects.
                // Prefer the character-item's current race if present
                // so live edits are visible in the table.
                const existingRaceId =
                  item.race && typeof item.race === 'object'
                    ? item.race.id
                    : item.race

                const staticRace = static_item.race ?? null
                const staticRaceId =
                  staticRace && typeof staticRace === 'object'
                    ? staticRace.id
                    : staticRace

                item.race = existingRaceId ?? staticRaceId ?? null

                // numeric value range and currency
                if (
                  item.value_lower === null ||
                  item.value_lower === undefined
                ) {
                  item.value_lower = static_item.value_lower ?? item.value_lower
                }
                if (
                  item.value_upper === null ||
                  item.value_upper === undefined
                ) {
                  item.value_upper = static_item.value_upper ?? item.value_upper
                }
                if (
                  item.estimated_value === null ||
                  item.estimated_value === undefined
                ) {
                  item.estimated_value =
                    static_item.estimated_value ?? item.estimated_value
                }
                if (isEmpty(item.currency)) {
                  item.currency = static_item.currency ?? item.currency
                }

                item.base_item = this.buildArtefactBaseItemDisplay(item)
              }

              item.value = this.buildItemValueDisplay(item)

              // storage related
              const resolvedContainerSize =
                item.container_size ?? static_item.container_size ?? null

              if (resolvedContainerSize) {
                item.container_capacity = resolvedContainerSize.capacity
                item.container_size_short = resolvedContainerSize.size_short
                item.container_size_long = resolvedContainerSize.size
                item.container_size = resolvedContainerSize
              } else {
                item.container_size_short = ''
                item.container_size_long = ''
                item.container_capacity = 0
                item.container_contents_count_direct = 0
                item.container_contents_count_total = 0
                item.container_contents_enc = 0
              }
            }
          } else {
            console.error('NO STATIC ITEM FOR ITEM = ', item.name)
          }

          // ---------------------------------------------------------------------------------
          // NB: removed from static item block as for artefacts there will be no static item

          // --------------------------------------------------------
          // derived flags that depend on character-level relationships
          // --------------------------------------------------------

          // ensure worn flag set if worn location set (cover new items)
          if (!isEmpty(item.worn_location)) {
            item.worn = true
            item.stored = false
            item.attached = false
          } else {
            item.worn = false
          }

          // ensure stored flag set if container set (cover new items)
          if (!isEmpty(item.container)) {
            item.stored = true
            item.worn = false
            item.attached = false
          } else {
            item.stored = false
          }

          // ensure attached flag set if attach location set (cover new items)
          if (!isEmpty(item.attach_location)) {
            item.attached = true
            item.worn = false
            item.stored = false
          } else {
            item.attached = false
          }

          const rawTallyQty = Number(item.tally_qty ?? 0)
          const isZeroQtyTallyable =
            rawTallyQty === 0 &&
            (item.tallyable === true ||
              item.tallyable === 1 ||
              item.tallyable === '1' ||
              item.tallyable === 'true')

          // Zero-qty tallyable items do not need a held / worn / stored / attached state.
          // This keeps skill checks aligned with the item-location UI, which already treats
          // quantity-zero tallyable items as exempt from location selection.
          if (
            !isZeroQtyTallyable &&
            !item.held &&
            !item.worn &&
            !item.stored &&
            !item.attached &&
            !item.equipped
          ) {
            item.no_location = true
          } else {
            item.no_location = false
          }

          // magic flag: true when this character item has any
          // integrated rune (linked via character_item) or any
          // item-granted spell linked to it. Uses character-level
          // runes/spells so that toggling rune integration/activation
          // or adding/removing spells is reflected on reload.
          const hasPowerStorage = Number(item.pwr_storage ?? 0) > 0
          const hasItemTraits = Array.isArray(item.item_traits)
            ? item.item_traits.length > 0
            : false
          const itemId = item.id
          if (itemId !== undefined && itemId !== null) {
            const normalizedItemId = String(itemId)
            // any rune linked to this item marks it as magic;
            // integration/active state is not required for the
            // item-level magic flag.
            const itemRunes = Array.isArray(character?.runes)
              ? character.runes.filter((r) => {
                  if (!r) return false
                  const linkedItemId =
                    r.character_item ?? r.character_item_id ?? null
                  return (
                    linkedItemId !== null &&
                    linkedItemId !== undefined &&
                    String(linkedItemId) === normalizedItemId
                  )
                })
              : []

            const itemSpells = Array.isArray(character?.spells)
              ? character.spells.filter((s) => {
                  if (!s) return false
                  const linkedItemId =
                    s.character_item ?? s.character_item_id ?? null
                  return (
                    linkedItemId !== null &&
                    linkedItemId !== undefined &&
                    String(linkedItemId) === normalizedItemId
                  )
                })
              : []

            item.rune_name = Array.from(
              new Set(
                itemRunes
                  .map((r) =>
                    (r?.name || '')
                      .toString()
                      .trim()
                      .replace(/\s+Rune$/i, ''),
                  )
                  .filter((name) => !isEmpty(name)),
              ),
            ).join(', ')

            item.magic =
              hasPowerStorage ||
              itemRunes.length > 0 ||
              itemSpells.length > 0 ||
              hasItemTraits
          } else {
            item.rune_name = ''
            item.magic = hasPowerStorage || hasItemTraits
          }

          return item
        })
        .sort((a, b) => {
          const getItemLabel = (item) => item.display_name || item.name || ''

          if (this.item_type === 'All') {
            const typeA = a.effective_type || a.type || ''
            const typeB = b.effective_type || b.type || ''
            const typeCompare = typeA.localeCompare(typeB)

            if (typeCompare !== 0) {
              return typeCompare
            }
          }

          return getItemLabel(a).localeCompare(getItemLabel(b))
        })

      const isTruthyFlag = (value) =>
        value === true || value === 1 || value === '1' || value === 'true'

      const heldCount = sortedItems.filter((item) =>
        isTruthyFlag(item.held),
      ).length

      this.items = sortedItems.map((item) => {
        const displayItem = { ...item }
        displayItem.held_yes = isTruthyFlag(displayItem.held)
        displayItem.over_held_limit =
          heldCount > 2 && isTruthyFlag(displayItem.held)

        if (displayItem.over_held_limit) {
          displayItem.no_location = true
        }

        return displayItem
      })

      // console.log('PPPPPP ITEMS = ', window.$deepCopy(this.items))
    },
    //========================================================
    // toggle details handler
    //------------------------
    toggleDetailsHandler(toggle) {
      console.log('-------------------------')
      console.log('IN items toggleDetailsHandler = ', toggle)
      // on = details / off = summary
      let i = 0
      let colNumbers = []
      //
      if (toggle) {
        console.log('TOGGLE DETAILS ON')
        colNumbers = getItemDetailCols(this.active_tab, this.headings)
      } else {
        console.log('TOGGLE DETAILS OFF')
        colNumbers = getItemSummaryCols(this.active_tab, this.headings)
      }
      //---------------
      // hide all cols
      //---------------
      this.headings.map((a) => (a.hidden = true))
      //--------------------
      // show specific cols
      //--------------------
      while (i < colNumbers.length) {
        // console.log('SHOWING COL = ', colNumbers[i], " field = ", this.headings[colNumbers[i]].field)
        this.headings[colNumbers[i]].hidden = false
        i++
      }
    },
    //========================================================
    // handle show item panel
    //-------------------------
    handleShowItemPanel(character_item) {
      console.log('IN handleShowItemPanel')
      this.character_item = character_item
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // handle add row
    //----------------
    handleAddRow() {
      console.log('IN handleAddRow')
      // this.character_item = character_item
      // this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // handle scroll table
    //----------------------
    handleScrollTable(dir) {
      // console.log('IN SheetItems handleScrollTable dir = ', dir)
      // call method on TableItems via ref
      this.$refs.TableItems.handleScrollTable(dir)
    },
    getHorizontalScrollState() {
      const tableRef = this.$refs.TableItems
      if (
        !tableRef ||
        typeof tableRef.getHorizontalScrollState !== 'function'
      ) {
        return { canScrollLeft: false, canScrollRight: false }
      }

      return tableRef.getHorizontalScrollState()
    },
    getVerticalScrollState() {
      const tableRef = this.$refs.TableItems
      if (!tableRef || typeof tableRef.getVerticalScrollState !== 'function') {
        return { canScrollDown: false, canScrollUp: false }
      }

      return tableRef.getVerticalScrollState()
    },
    //========================================================
    // forwardEmitOpenAdjs
    //---------------------
    forwardEmitOpenAdjs(data) {
      this.$emit('adjust-character-item', data)
    },
    //========================================================
    // handle item panel close
    //-------------------------
    handleItemPanelClose() {
      console.log('IN handleItemPanelClose...')
      // this.SetView('items')
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    handleConfirmItemOk({ displayName } = {}) {
      const name = (displayName || '').trim() || 'Item'
      showNotification(`${name} updated.`)
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    console.log('item type = ', this.item_type)
    //----------------------
    // load common headings
    //----------------------
    if (this.headings.length === 0) {
      this.headings.push(...this.headings_common)
    }
    //----------------------
    // prime toggle details
    //----------------------
    this.toggleDetailsHandler(this.toggleDetails)
    this.headingsReady = true
    //========================================================
    // toggle item details handler
    //------------------------------
    this.emitter.on('toggle-item-details', () => {
      console.log('IN items toggle-item-details')
      this.toggleDetailsHandler()
    })
  },
}
</script>

<style scoped>
.bb-sheet-header-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  margin-bottom: 0.35rem;
}

.bb-sheet-search-slot {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.bb-sheet-search-wrap {
  min-width: 18rem;
}

.bb-sheet-search-shell {
  margin-bottom: 0;
}

.bb-sheet-search-input {
  min-width: 14rem;
  width: 100%;
}

.bb-sheet-header-row .bb-sheet-title {
  width: 100%;
  text-align: center;
}

@media (max-width: 768px) {
  .bb-sheet-header-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .bb-sheet-search-slot {
    position: static;
    transform: none;
  }
}
</style>
